import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Checkbox from "@mui/material/Checkbox";
import AwsWafCaptcha from "components/AwsWafCaptcha";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import IllustrationLayout from "layouts/authenticationLayout/index";
import { isWafConfigured } from "config/authApi";
import { signUp } from "services/authRestService";
import { isValidEmail } from "utils/validation";
import rocket from "assets/images/illustrations/rocket-white.png";

function SignUp() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [agreement, setAgreement] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [wafToken, setWafToken] = useState(null);
  const [captchaResetKey, setCaptchaResetKey] = useState(0);

  const captchaRequired = isWafConfigured();
  const showCaptcha = Boolean(name.trim() && isValidEmail(email));
  const formSignature = `${name.trim().toLowerCase()}|${email.trim().toLowerCase()}`;
  const previousFormSignatureRef = useRef(formSignature);
  const canSubmit =
    name.trim() &&
    email.trim() &&
    agreement &&
    (!captchaRequired || wafToken) &&
    !isSubmitting;

  useEffect(() => {
    if (!captchaRequired) {
      setWafToken(null);
    }
  }, [captchaRequired]);

  useEffect(() => {
    if (!showCaptcha) {
      previousFormSignatureRef.current = "";
      setWafToken(null);
      return;
    }

    if (
      previousFormSignatureRef.current &&
      previousFormSignatureRef.current !== formSignature
    ) {
      setWafToken(null);
      setCaptchaResetKey((current) => current + 1);
    }

    previousFormSignatureRef.current = formSignature;
  }, [showCaptcha, formSignature]);

  const handleToggleAgreement = () => setAgreement((current) => !current);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    if (!agreement) {
      setError("Please accept the Terms and Conditions to continue.");
      return;
    }

    if (captchaRequired && !wafToken) {
      setError("Please complete the security check before signing up.");
      return;
    }

    setIsSubmitting(true);

    try {
      await signUp({
        name: name.trim(),
        email: email.trim(),
      });

      setSuccess(
        "Account registration started. Check your email for verification instructions, then sign in."
      );
      setName("");
      setEmail("");
      setAgreement(false);
      setWafToken(null);
      setCaptchaResetKey((current) => current + 1);
    } catch (err) {
      setError(err?.message || "Sign up failed. Please try again.");
      setWafToken(null);
      setCaptchaResetKey((current) => current + 1);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <IllustrationLayout
      title="Sign Up"
      description="Enter your name and email to register"
      illustration={{
        image: rocket,
        title: "Your journey starts here",
        description:
          "Just as it takes a company to sustain a product, it takes a community to sustain a protocol.",
      }}
    >
      <SoftBox component="form" role="form" onSubmit={handleSubmit} noValidate>
        {error && (
          <SoftBox mb={2} p={2} bgColor="error" borderRadius="lg">
            <SoftTypography variant="body2" color="white">
              {error}
            </SoftTypography>
          </SoftBox>
        )}

        {success && (
          <SoftBox mb={2} p={2} bgColor="success" borderRadius="lg">
            <SoftTypography variant="body2" color="white">
              {success}
            </SoftTypography>
          </SoftBox>
        )}

        <SoftBox mb={2}>
          <SoftInput
            placeholder="Name"
            size="large"
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={isSubmitting}
            required
            autoComplete="name"
          />
        </SoftBox>
        <SoftBox mb={2}>
          <SoftInput
            type="email"
            placeholder="Email"
            size="large"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={isSubmitting}
            required
            autoComplete="email"
          />
        </SoftBox>

        <AwsWafCaptcha
          enabled={showCaptcha}
          resetKey={captchaResetKey}
          onToken={setWafToken}
          onError={() => setWafToken(null)}
        />

        <SoftBox display="flex" alignItems="center">
          <Checkbox checked={agreement} onChange={handleToggleAgreement} />
          <SoftTypography
            variant="button"
            fontWeight="regular"
            onClick={handleToggleAgreement}
            sx={{ cursor: "pointer", userSelect: "none" }}
          >
            &nbsp;&nbsp;I agree the&nbsp;
          </SoftTypography>
          <SoftTypography
            component="a"
            href="#"
            variant="button"
            fontWeight="bold"
            textGradient
          >
            Terms and Conditions
          </SoftTypography>
        </SoftBox>
        <SoftBox mt={4} mb={1}>
          <SoftButton
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            disabled={!canSubmit}
          >
            {isSubmitting ? "Creating account..." : "Sign up"}
          </SoftButton>
        </SoftBox>
        <SoftBox mt={3} textAlign="center">
          <SoftTypography variant="button" color="text" fontWeight="regular">
            Already have an account?&nbsp;
            <SoftTypography
              component={Link}
              to="/sign-in"
              variant="button"
              color="secondary"
              fontWeight="bold"
            >
              Sign in
            </SoftTypography>
          </SoftTypography>
        </SoftBox>
        {success && (
          <SoftBox mt={2} textAlign="center">
            <SoftButton
              variant="outlined"
              color="secondary"
              size="small"
              onClick={() => navigate("/sign-in")}
            >
              Go to sign in
            </SoftButton>
          </SoftBox>
        )}
      </SoftBox>
    </IllustrationLayout>
  );
}

export default SignUp;
